import { computed, watch } from 'vue';
import { RouteRecordRaw } from 'vue-router';
import { RouteData, useNavigation } from '~/composables/useNavigation';
import { Lang, Logger, Router } from '~/core';
import { getRouteParams } from '~/helpers';
import { hasAddon } from '~/plugins/addon';
import { globalMiddlewares } from '~/routes';
import { useModelsStore } from '~/stores/models';
import { useSettingsStore } from '~/stores/settings';
import { useUserStore } from '~/stores/user';

// Create page title computation function - will be called after Pinia is available
function createPageTitle() {
    return computed(() => {
        const settingsStore = useSettingsStore();
        const modelsStore = useModelsStore();
        const physgun = hasAddon('physgun');

        let branding = settingsStore.branding?.name || 'WISP';
        if (physgun && branding === 'WISP') branding = 'Physgun';

        const routeName = Router.currentRoute.value.name?.toString();
        const routeMatched = Router.currentRoute.value.matched;
        if (!routeName || routeMatched.length < 0) return `${branding} | Game Panel`;

        const routeTranslation = Lang.global.t(`navigation.${routeName}.title`);

        // TODO: this should also consider (Tabber)Passthrough with separation of admin and client area
        const route = routeMatched[routeMatched.length - 1];
        for (const path of route.path.split('/')) {
            if (!path.startsWith(':')) continue;

            const paramName = path.substring(1);
            const allModels = modelsStore.getAllModels;
            if (paramName in allModels) {
                const model = (allModels as any)[paramName];
                if (!model.name) continue;

                return `${branding} | ${model.name} - ${routeTranslation}`;
            }
        }
        return `${branding} | ${routeTranslation}`;
    });
}

// Initialize page title watching - will be called after Pinia is available
export function initializePageTitle() {
    const pageTitle = createPageTitle();
    watch(pageTitle, (value: string) => (document.title = value));
}

function normalizeMiddleware(middleware: Middleware | [Middleware, number]) {
    return Array.isArray(middleware) ? middleware : [middleware, 0];
}

function getMiddlewares(global: PrioritizationMiddleware = [], route: undefined | PrioritizationMiddleware = []) {
    return global
        .map(normalizeMiddleware)
        .concat(route.map(normalizeMiddleware))
        .sort((a, b) => (b[1] as number) - (a[1] as number)) // TypeScript plz, why do you think a[1] is not a number???
        .map((middleware) => middleware[0]) as Middleware[];
}

Router.beforeEach(async (to, from, next) => {
    const userStore = useUserStore();
    const settingsStore = useSettingsStore();

    const initializedUser = userStore.isInitialized;
    const initializedSettings = settingsStore.isInitialized;
    if (!initializedUser || !initializedSettings) {
        Logger.debug('Router', 'User and/or settings data is not available yet, waiting...');

        await Promise.all([
            new Promise<void>((resolve) => {
                if (initializedUser) return resolve();

                const finished = userStore.$onAction(({ name, after }: any) => {
                    if (name === 'markInitialized') {
                        after(() => {
                            resolve();
                            finished();
                        });
                    }
                });
            }),
            new Promise<void>((resolve) => {
                if (initializedSettings) return resolve();

                const finished = settingsStore.$onAction(({ name, after }: any) => {
                    if (name === 'markInitialized') {
                        after(() => {
                            resolve();
                            finished();
                        });
                    }
                });
            }),
        ]);
    }

    const middlewares = getMiddlewares(globalMiddlewares, to.meta?.middlewares as PrioritizationMiddleware | undefined);
    Logger.debug('Router', `Visit ${from.fullPath} -> ${to.fullPath} [middlewares: ${middlewares.map((a) => a.name()).join(', ')}]`);

    for (const middleware of middlewares) {
        const res: any = await middleware.run(to, from);
        if (res !== undefined) {
            Logger.debug('Router', `Middleware ${middleware.name()} failed.`);
            return next(res);
        }
    }

    return next();
});

Router.afterEach(async (to, from, failure) => {
    if (!failure && from.matched.length > 0) {
        useNavigation().setPreviousRoute(
            typeof from.name === 'string'
                ? {
                      name: from.name,
                      params: from.params,
                      query: from.query,
                      hash: from.hash,
                  }
                : {
                      path: from.path,
                      query: from.query,
                      hash: from.hash,
                  },
        );
    }

    const navigation = useNavigation();

    if (to.matched.length > 0) {
        const routes: RouteData[] = [];

        // We need to find the furthest `showChildrenInNavbar` element to be able to
        // know which children the navbar should show.
        for (let i = to.matched.length - 1; i >= 0; i--) {
            const match = to.matched[i];
            if (!match.meta?.showChildrenInNavbar) continue;

            const getRouteData = async (child: RouteRecordRaw) => {
                const userStore = useUserStore();

                const passthrough = !!child.meta?.passthrough;
                let realChild: any = child;
                if (passthrough) {
                    realChild = child.children?.[0];
                }

                if (!child.name || !realChild) {
                    Logger.warn('Router', `Route ${child.path || '<blank path>'} is missing required data for navbar.`);
                    return;
                }

                if (child.meta?.hidden) return;
                if (child.meta?.adminOnly && !userStore.data?.rootAdmin && !userStore.data?.supportOp) return;
                if (child.meta?.whmcsOnly && window.location.hostname !== 'gamecp.physgun.com') return;

                const childName = child.name.toString();
                const toName = to.name?.toString() ?? '';
                const isExactMatch = childName === toName;
                const isDescendant = toName.startsWith(`${childName}.`);

                return {
                    name: realChild.name.toString(),
                    displayName: realChild.name.toString(),
                    active: passthrough ? isDescendant : isExactMatch || isDescendant,
                    params: getRouteParams(`${match.path}/${child.path}`, to.params),
                    permission: child.meta?.permission as string | undefined,
                    feature: child.meta?.feature as string | undefined,
                    addon: child.meta?.addon as string | undefined,
                    eggTag: child.meta?.eggTag as string | string[] | undefined,
                    nestTag: child.meta?.nestTag as string | string[] | undefined,
                    icon: child.meta?.icon as string | undefined,
                    allowWhenInstalling: child.meta?.allowWhenInstalling as boolean | undefined,
                };
            };

            for (const child of match.children) {
                if (child.meta?.showAsCategory) {
                    if (!child.name) {
                        Logger.warn('Router', `Route ${child.path || '<blank path>'} is configured to be shown as category, without a name.`);
                        continue;
                    }

                    if (!child.children) {
                        Logger.warn('Router', `Route ${child.path || '<blank path>'} is configured to be shown as category, without children.`);
                        continue;
                    }

                    const categoryRoutes: RouteData[] = [];
                    for (const childRoute of child.children) {
                        const routeData = await getRouteData(childRoute);
                        if (!routeData) continue;

                        categoryRoutes.push(routeData);
                    }

                    routes.push({
                        name: child.name.toString(),
                        displayName: child.name.toString(),
                        active: categoryRoutes.some((a) => a.active),
                        permission: child.meta?.permission as string | undefined,
                        feature: child.meta?.feature as string | undefined,
                        addon: child.meta?.addon as string | undefined,
                        eggTag: child.meta?.eggTag as string | string[] | undefined,
                        nestTag: child.meta?.nestTag as string | string[] | undefined,
                        icon: child.meta?.icon as string | undefined,

                        routes: categoryRoutes,
                    });
                } else {
                    const routeData = await getRouteData(child);
                    if (!routeData) continue;

                    routes.push(routeData);
                }
            }
            break;
        }

        navigation.setRoutes(routes);
    }

    const middlewares = getMiddlewares(globalMiddlewares, to.meta?.middlewares as PrioritizationMiddleware | undefined);
    for (const middleware of middlewares) {
        if (middleware.postRun) await middleware.postRun();
    }
});
